import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { Context } from "context/Wrapper";
import { collection, query, where, getDocs, orderBy, limit, getDoc, doc as docRef } from "firebase/firestore";
import { db } from "config/config";
import AdvancedTable from "components/AdvancedTable";
import tableConfig from "config/advancedTableColumnsControl.json";
import { getBuildingBaseColumns, getBuildingDateUpdatedColumn } from "config/buildingColumns";
import useCustomParams from "components/shared/useCustomParams";


export const DashboardBuildingsTable = () => {
  const context = useContext(Context);
  const navigate = useNavigate();
  const { groupId, isPro, userId } = context?.state?.userProfile || {};
  const { showAllDataFlag } = context?.state || {};
  const { params } = useCustomParams();

  const tableId = "dashboard-buildings";
  const pageSize = Number(params.get(`${tableId}_pageSize`)) || 10;
  const page = Number(params.get(`${tableId}_page`)) || 0;
  const fetchLimit = (page + 1) * pageSize;


  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentBuildings = async () => {
      try {
        setLoading(true);
        const buildingsRef = collection(db, "buildings");

        const conditions = [];
        if (showAllDataFlag && groupId) {
          conditions.push(where("groupId", "==", groupId));
        } else if (!showAllDataFlag && userId) {
          conditions.push(where("userId", "==", userId));
        }
        conditions.push(orderBy("dateUpdated", "desc"));
        conditions.push(limit(fetchLimit));

        const q = query(buildingsRef, ...conditions);
        const snapshot = await getDocs(q);

        const buildingsData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            let ownerString = "";

            if (data.owner && typeof data.owner === 'string' && data.owner.trim() !== '') {
              try {
                const ownerDoc = await getDoc(docRef(db, "contacts", data.owner));
                ownerString = ownerDoc.exists() ? ownerDoc.data().businessName || "" : "";
              } catch (error) {
                console.error("Error fetching owner:", error);
              }
            }

            const fullAddress = [
              (data.houseNumber || "") + " " + (data.streetName || ""),
              data.city,
              data.state,
              data.ZIP,
            ].filter(Boolean).join(", ");

            return {
              ...data,
              id: doc.id,
              fullAddress,
              ownerString,
              ownerString_lc: ownerString.toLowerCase(),
              fullAddress_lc: fullAddress.toLowerCase(),
            };
          })
        );
        setBuildings(buildingsData);
      } catch (error) {
        console.error("Error fetching buildings:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchRecentBuildings();
    }
  }, [userId, showAllDataFlag, groupId, isPro, page, pageSize]);

  const dateUpdatedColumn = {
    field: "dateUpdated",
    headerName: "Last Updated",
    renderCell: ({ row }) => {
      if (!row.dateUpdated) return "";
      const d = typeof row.dateUpdated === "number"
        ? new Date(row.dateUpdated)
        : row.dateUpdated.toDate();
      return d.toISOString().split("T")[0];
    },
  };

  const columns = [
    ...getBuildingBaseColumns(),
    ...(tableConfig.buildings.dateUpdated ? [getBuildingDateUpdatedColumn()] : []),
  ];

  if (loading) {
    return <Typography>Loading buildings...</Typography>;
  }

  return (
    <Box sx={{
      // Hide toolbar
      '& .MuiDataGrid-toolbarContainer': {
        display: 'none !important'
      },
      '& .MuiTablePagination-actions': {
        display: 'none !important'
      },
      '& .MuiTablePagination-displayedRows': {
        display: 'none !important'
      },
      '& .MuiTablePagination-selectLabel': {
        display: 'block !important'
      },
      '& .MuiTablePagination-select': {
        display: 'block !important'
      }
    }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Recent Buildings</Typography>

      <AdvancedTable
        data={buildings}
        collectionName="buildings"
        columnsRaw={columns}
        rowCount={buildings.length}
        isInactive={false}
        tableId="dashboard-buildings"
        pageSize={10}
        hideActions={true}
        disableSorting={true}
        disableColumnMenu={true}

      />
    </Box>
  );
};